import { ArrowDownIcon, ArrowUpIcon } from '@radix-ui/react-icons';
import type { Column } from '@tanstack/react-table';
import { useState } from 'react';

import { SortCarats } from '@/assets/icons/sort-carats';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLocale } from '@/lib/hooks/use-locale';
import { cn } from '@/lib/utils';

type DataTableSortHeaderProps<TData, TValue> = {
    column: Column<TData, TValue>;
    title: string;
    align?: 'left' | 'center' | 'right';
} & React.HTMLAttributes<HTMLDivElement>;

export function DataTableSortHeader<TData, TValue>({
    column,
    title,
    className,
    align = 'left',
}: DataTableSortHeaderProps<TData, TValue>) {
    const { t } = useLocale();
    const [open, setOpen] = useState(false);

    if (!column.getCanSort()) {
        return <div className={cn(className)}>{title}</div>;
    }

    const isSelected = column.getIsSorted();

    return (
        <div
            className={cn(
                'flex items-center',
                align === 'left' && '-ml-2 justify-start text-left',
                align === 'center' && 'justify-center text-center',
                align === 'right' && '-mr-2 justify-end text-right',
                className,
            )}
        >
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                            'data-[state=open]:bg-accent items-center outline-hidden! focus:border-transparent! focus:ring-0!',
                            open || isSelected ? 'text-neutral-800' : 'text-neutral-600',
                        )}
                    >
                        <span>{title}</span>
                        {column.getIsSorted() === 'desc' ? (
                            <ArrowDownIcon
                                className={cn(
                                    'ml-1 h-3 w-3 ',
                                    open || isSelected ? 'stroke-neutral-800' : 'stroke-neutral-300',
                                )}
                            />
                        ) : column.getIsSorted() === 'asc' ? (
                            <ArrowUpIcon
                                className={cn(
                                    'ml-1 h-3 w-3 ',
                                    open || isSelected ? 'stroke-neutral-800' : 'stroke-neutral-300',
                                )}
                            />
                        ) : (
                            <SortCarats
                                className={cn(
                                    'ml-1 h-2.5 w-2.5 ',
                                    open || isSelected
                                        ? 'fill-neutral-800 stroke-neutral-800'
                                        : 'fill-neutral-300 stroke-neutral-300',
                                )}
                            />
                        )}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
                        <ArrowUpIcon className="mr-2 h-4 w-4 fill-neutral-800 stroke-neutral-800 text-neutral-800" />
                        {t('ascending')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
                        <ArrowDownIcon className=" mr-2 h-4 w-4 fill-neutral-800 stroke-neutral-800 text-neutral-800" />
                        {t('descending')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
